import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import {
  employmentTypeOptions,
  jobStatusUpdateOptions,
  workModeOptions,
} from "../../../helpers/jobPortalMeta";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import LocationPicker from "../../../components/LocationPicker";

const initialForm = {
  jobTitle: "",
  jobRole: "",
  employmentType: "full_time",
  workMode: "onsite",
  state: "",
  statename: "",
  city: "",
  country: "",
  pincode: "",
  latitude: "",
  longitude: "",
  zone: "",
  openings: 1,
  experienceMin: 0,
  experienceMax: 0,
  salaryMin: 0,
  salaryMax: 0,
  qualification: "",
  qualifications: [],
  qualificationDetails: [],
  genderRequirement: 0,
  englishRequirement: 0,
  relocationAllowed: false,
  jobDescription: "",
  companyName: "",
  contactPerson: "",
  phone: "",
  email: "",
  address: "",
  companyProfile: "",
  companySize: "",
  jobStatus: "active",
  adminApprovalStatus: "approved",
  skillsRequired: [],
};

const genderOptions = [
  { value: 0, label: "Any", helper: "Open to all applicants" },
  { value: 2, label: "Male", helper: "Male candidates only" },
  { value: 3, label: "Female", helper: "Female candidates only" },
];

const englishOptions = [
  { value: 0, label: "Optional", helper: "English not required" },
  { value: 1, label: "Basic", helper: "Simple workplace words" },
  { value: 2, label: "Conversational", helper: "Can speak with patients or team" },
  { value: 3, label: "Good", helper: "Clear professional communication" },
  { value: 4, label: "Fluent", helper: "Strong spoken and written English" },
];

const radioCardStyle = {
  border: "1px solid #d9e2ef",
  borderRadius: 10,
  padding: "12px 14px",
   cursor: "pointer",
  background: "#fff",
  transition: "all 0.18s ease",
};

const selectedRadioCardStyle = {
  borderColor: "#0d6efd",
  background: "#f0f6ff",
  boxShadow: "0 8px 20px rgba(13, 110, 253, 0.12)",
};

const jobRoleSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderColor: state.isFocused ? "#0d6efd" : "#d9e2ef",
    borderRadius: 8,
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13, 110, 253, 0.16)" : "none",
    "&:hover": {
      borderColor: "#0d6efd",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#98a2b3",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16)",
  }),
  groupHeading: (base) => ({
    ...base,
    margin: 0,
    padding: "9px 12px",
    background: "#eef2f7",
    color: "#344054",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
  }),
  option: (base, state) => ({
    ...base,
    padding: "11px 16px",
    fontSize: 15,
    color: "#1f2937",
    background: state.isSelected
      ? "#e7f0ff"
      : state.isFocused
      ? "#f5f8ff"
      : "#fff",
    cursor: "pointer",
  }),
};

const insightCardStyle = {
  border: "1px solid #e4e7ec",
  borderRadius: 12,
  background: "#fff",
  padding: 20,
};

const reachPanelStyle = {
  border: "1px solid #5bc66f",
  borderRadius: 8,
  background: "#f5fff7",
  padding: "14px 16px",
};

const salaryTrendStyle = {
  borderRadius: 10,
  background: "#fbfbfc",
  padding: "18px 16px",
};

const formatCurrency = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) {
    return "--";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numberValue);
};

const getReachStatus = ({ count, score, status }) => {
  const numericCount = Number(count) || 0;
  const hasScore = score !== null && score !== undefined && score !== "";
  const numericScore = hasScore
    ? Number(score) || 0
    : numericCount >= 5
    ? 100
    : numericCount >= 3
    ? 60
    : numericCount >= 1
    ? 45
    : 0;
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "high" || numericScore >= 70) {
    return {
      label: "High Response",
      color: "#34a853",
      needle: "rotate(28deg)",
      helper: "Based on your requirement parameters",
    };
  }

  if (normalizedStatus === "medium" || numericScore >= 40) {
    return {
      label: "Medium Response",
      color: "#f9ab00",
      needle: "rotate(0deg)",
      helper: "Based on your requirement parameters",
    };
  }

  return {
    label: "Low Response",
    color: "#ea4335",
    needle: "rotate(-32deg)",
    helper: "Based on your requirement parameters",
  };
};

const getSalaryRecommendation = ({
  offeredMin,
  offeredMax,
  marketAverageMin,
  marketAverageMax,
}) => {
  const minOffer = Number(offeredMin);
  const maxOffer = Number(offeredMax);

  if (
    !Number.isFinite(minOffer) ||
    !Number.isFinite(maxOffer) ||
    minOffer <= 0 ||
    maxOffer <= 0 ||
    !marketAverageMin ||
    !marketAverageMax
  ) {
    if (
      Number.isFinite(minOffer) &&
      Number.isFinite(maxOffer) &&
      minOffer > 0 &&
      maxOffer > 0
    ) {
      return {
        tone: "muted",
        title: "No market salary found",
        message: "No matching job salary data is available for this role, experience and skill match.",
      };
    }

    return {
      tone: "muted",
      title: "Enter salary to compare",
      message: "Salary suggestion will appear after matching jobs are found.",
    };
  }

  if (maxOffer < marketAverageMin) {
    return {
      tone: "danger",
      title: "You are offering lower salary",
      message: "Suggestion: increase salary to improve job response.",
    };
  }

  if (minOffer > marketAverageMax) {
    return {
      tone: "success",
      title: "You are offering higher salary",
      message: "Suggestion: you may reduce salary slightly and still stay competitive.",
    };
  }

  return {
    tone: "primary",
    title: "Your salary is aligned",
    message: "Suggestion: keep this salary range for a balanced response.",
  };
};

const RadioCardGroup = ({ label, name, value, options, onChange, columns = "col-md-4" }) => (
  <div className="col-12">
    <label className="form-label d-block">{label}</label>
    <div className="row g-2">
      {options.map((option) => {
        const isSelected = String(value) === String(option.value);
        return (
          <div className={columns} key={option.value}>
            <label
              className="d-flex align-items-start gap-2 h-100"
              style={{
                ...radioCardStyle,
                ...(isSelected ? selectedRadioCardStyle : {}),
              }}
            >
              <input
                className="form-check-input mt-1"
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={onChange}
              />
              <span>
                <span className="d-block fw-semibold text-dark">
                  {option.label}
                </span>
                {option.helper && (
                  <span className="d-block small text-muted">
                    {option.helper}
                  </span>
                )}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  </div>
);

const RecruiterJobForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [formData, setFormData] = useState(initialForm);
  const [zones, setZones] = useState([]);
  const [skills, setSkills] = useState([]);
  const [functionalAreas, setFunctionalAreas] = useState([]);
  const [educationList, setEducationList] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [customSkill, setCustomSkill] = useState("");
  const [jobTitleAutoFilled, setJobTitleAutoFilled] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insights, setInsights] = useState({
    jobs: [],
    total: 0,
    salaryMin: null,
    salaryMax: null,
    salaryAverageMin: null,
    salaryAverageMax: null,
    reachScore: 0,
    reachStatus: "low",
    averageSkillMatch: null,
  });

  const jobRoleOptions = useMemo(() => {
    const groupedRoles = new Map();

    functionalAreas.forEach((item) => {
      const groupLabel = item.jobRoleCategory || "Suggested Job Roles";
      const option = {
        value: item.name,
        label: item.name,
      };

      if (!groupedRoles.has(groupLabel)) {
        groupedRoles.set(groupLabel, []);
      }

      groupedRoles.get(groupLabel).push(option);
    });

    return Array.from(groupedRoles, ([label, options]) => ({
      label,
      options,
    }));
  }, [functionalAreas]);

  const selectedJobRoleOption = useMemo(
    () =>
      jobRoleOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === formData.jobRole) || null,
    [jobRoleOptions, formData.jobRole]
  );

  const educationOptions = useMemo(() => {
    const groupedEducation = new Map();

    educationList.forEach((item) => {
      const groupLabel = item.educationCategory || "Education";
      const option = {
        value: item.name,
        label: item.name,
      };

      if (!groupedEducation.has(groupLabel)) {
        groupedEducation.set(groupLabel, []);
      }

      groupedEducation.get(groupLabel).push(option);
    });

    return Array.from(groupedEducation, ([label, options]) => ({
      label,
      options,
    }));
  }, [educationList]);

  const selectedQualificationOptions = useMemo(() => {
    const selectedValues = Array.isArray(formData.qualifications)
      ? formData.qualifications
      : [];

    return educationOptions
      .flatMap((group) => group.options)
      .filter((option) => selectedValues.includes(option.value));
  }, [educationOptions, formData.qualifications]);

  const salaryInsightReady =
    Number(formData.salaryMin) > 0 && Number(formData.salaryMax) > 0;
  const insightMatchReady =
    Boolean(formData.jobRole) &&
    formData.experienceMin !== "" &&
    formData.experienceMin !== null &&
    formData.experienceMax !== "" &&
    formData.experienceMax !== null;

  const reachStatus = getReachStatus({
    count: insights.total,
    score: insights.reachScore,
    status: insights.reachStatus,
  });
  const salaryRecommendation = useMemo(
    () =>
      getSalaryRecommendation({
        offeredMin: formData.salaryMin,
        offeredMax: formData.salaryMax,
        marketAverageMin: insights.salaryAverageMin,
        marketAverageMax: insights.salaryAverageMax,
      }),
    [
      formData.salaryMin,
      formData.salaryMax,
      insights.salaryAverageMin,
      insights.salaryAverageMax,
    ]
  );

  const handleJobRoleChange = (selectedOption) => {
    const selectedRole = selectedOption?.value || "";
    const shouldAutoFillTitle =
      selectedRole && !jobTitleAutoFilled && !formData.jobTitle;

    setFormData((prev) => ({
      ...prev,
      jobRole: selectedRole,
      jobTitle: shouldAutoFillTitle ? selectedRole : prev.jobTitle,
      skillsRequired: [],
    }));

    if (shouldAutoFillTitle) {
      setJobTitleAutoFilled(true);
    }
  };

  const handleQualificationChange = (selectedOptions) => {
    const qualifications = (selectedOptions || []).map((option) => option.value);
    setFormData((prev) => {
      const previousDetails = Array.isArray(prev.qualificationDetails)
        ? prev.qualificationDetails
        : [];

      return {
        ...prev,
        qualifications,
        qualification: qualifications.join(", "),
        qualificationDetails: qualifications.map((qualification) => {
          const existingDetail = previousDetails.find(
            (detail) => detail.qualification === qualification
          );

          return (
            existingDetail || {
              qualification,
              startDate: "",
              endDate: "",
              collegeOrInstitute: "",
              schoolName: "",
            }
          );
        }),
      };
    });
  };

  const updateQualificationDetail = (qualification, field, value) => {
    setFormData((prev) => ({
      ...prev,
      qualificationDetails: (Array.isArray(prev.qualificationDetails)
        ? prev.qualificationDetails
        : []
      ).map((detail) =>
        detail.qualification === qualification
          ? { ...detail, [field]: value }
          : detail
      ),
    }));
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === "checkbox" && name === "skillsRequired") {
      setFormData((prev) => ({
        ...prev,
        skillsRequired: checked
          ? [...prev.skillsRequired, value]
          : prev.skillsRequired.filter((item) => item !== value),
      }));
      return;
    }

    if (name === "jobRole") {
      setFormData((prev) => ({
        ...prev,
        jobRole: value,
        skillsRequired: [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const applyJobLocation = (location) => {
    const selectedZone = zones.find(
      (zone) => zone._id === location.state || zone.name === location.statename
    );

    setFormData((prev) => ({
      ...prev,
      ...location,
      state: selectedZone?._id || location.state || prev.state,
      statename: selectedZone?.name || location.statename || prev.statename,
      city: location.city || prev.city,
      country: location.country || prev.country,
      pincode: location.pincode || prev.pincode,
      latitude: location.latitude || prev.latitude,
      longitude: location.longitude || prev.longitude,
      address: location.address || prev.address,
    }));
  };

  const addCustomSkill = () => {
    const skillName = customSkill.trim();
    if (!skillName) {
      return;
    }

    setFormData((prev) => {
      const alreadySelected = prev.skillsRequired.some(
        (skill) => skill.toLowerCase() === skillName.toLowerCase()
      );

      if (alreadySelected) {
        return prev;
      }

      return {
        ...prev,
        skillsRequired: [...prev.skillsRequired, skillName],
      };
    });
    setCustomSkill("");
  };

  const removeSkill = (skillName) => {
    setFormData((prev) => ({
      ...prev,
      skillsRequired: prev.skillsRequired.filter((skill) => skill !== skillName),
    }));
  };

  const fetchMasterData = async () => {
    try {
      setMasterDataLoading(true);
      const [
        areasResponse,
        zonesResponse,
        educationResponse,
      ] = await Promise.all([
        axiosInstance.get("/get-all-specialization"),
        axiosInstance.get("/get-all-zones"),
        axiosInstance.get("/get-all-education"),
      ]);

      setFunctionalAreas(areasResponse.data?.Specialization || []);
      setZones(zonesResponse.data?.Zones || []);
      setEducationList(educationResponse.data?.Education || []);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load job form data");
    } finally {
      setMasterDataLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get("/job-portal/subscription/me", authConfig);
      setSubscription(response.data?.data?.subscription || null);
    } catch (error) {
      console.error(error);
    }
  };

  const prefillRecruiterProfile = async () => {
    if (isEditMode) {
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.post("/auth-user", {
        id: authConfig.headers["x-auth-user-id"],
      });
      const existingUser = response.data?.existingUser;

      if (!existingUser) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        companyName: prev.companyName || existingUser.username || "",
        contactPerson: prev.contactPerson || existingUser.username || "",
        phone: prev.phone || existingUser.phone || "",
        email: prev.email || existingUser.email || "",
        address: prev.address || existingUser.address || "",
        country: prev.country || existingUser.country || "",
        pincode: prev.pincode || existingUser.pincode || "",
        latitude: prev.latitude || existingUser.latitude || "",
        longitude: prev.longitude || existingUser.longitude || "",
        companyProfile: prev.companyProfile || existingUser.about || "",
        companySize:
          prev.companySize || String(existingUser.numberOfBeds || ""),
        statename: prev.statename || existingUser.statename || "",
        city: prev.city || existingUser.city || "",
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchJobDetails = async () => {
    if (!isEditMode) {
      return;
    }

    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.get(`/job-portal/jobs/${id}`, authConfig);
      const job = response.data?.data?.job;

      if (!job) {
        throw new Error("Job not found");
      }

      const matchingZone = zones.find(
        (zone) => zone.name === (job.location?.state || "")
      );

      const qualifications = Array.isArray(job.qualifications)
        ? job.qualifications
        : (job.qualification || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

      setFormData((prev) => ({
        ...prev,
        jobTitle: job.title || "",
        jobRole: job.roleTitle || "",
        employmentType: job.employmentType || "full_time",
        workMode: job.workMode || "onsite",
        state: matchingZone?._id || "",
        statename: job.location?.state || "",
        city: job.location?.city || "",
        country: job.location?.country || job.company?.country || "",
        pincode: job.location?.pincode || job.company?.pincode || "",
        latitude: job.location?.latitude || job.company?.latitude || "",
        longitude: job.location?.longitude || job.company?.longitude || "",
        zone: job.location?.zone || "",
        openings: job.openings || 1,
        experienceMin: job.experience?.minimum || 0,
        experienceMax: job.experience?.maximum || 0,
        salaryMin: job.salary?.minimum || 0,
        salaryMax: job.salary?.maximum || 0,
        qualification: job.qualification || qualifications.join(", "),
        qualifications,
        qualificationDetails: Array.isArray(job.qualificationDetails)
          ? job.qualificationDetails
          : [],
        genderRequirement: job.candidatePreferences?.gender ?? 0,
        englishRequirement: job.candidatePreferences?.englishRequirement ?? 0,
        relocationAllowed: Boolean(job.candidatePreferences?.relocation),
        jobDescription: job.description || "",
        companyName: job.company?.name || "",
        contactPerson: job.company?.contactPerson || "",
        phone: job.company?.phone || "",
        email: job.company?.email || "",
        address: job.company?.address || "",
        companyProfile: job.company?.profile || "",
        companySize: job.company?.size || "",
        jobStatus: job.jobStatus || "active",
        adminApprovalStatus: job.adminApprovalStatus || "approved",
        skillsRequired: job.skills || [],
      }));
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to load job details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    prefillRecruiterProfile();
    fetchSubscription();
  }, []);

  useEffect(() => {
    const fetchSkillsByJobRole = async () => {
      if (!formData.jobRole) {
        setSkills([]);
        return;
      }

      try {
        const params = `?jobRole=${encodeURIComponent(formData.jobRole)}`;
        const response = await axiosInstance.get(`/get-all-skill${params}`);
        const nextSkills = response.data?.Skill || [];
        setSkills(nextSkills);
      } catch (error) {
        console.error(error);
        toast.error("Unable to load skills");
      }
    };

    fetchSkillsByJobRole();
  }, [formData.jobRole]);

  useEffect(() => {
    if (!masterDataLoading) {
      fetchJobDetails();
    }
  }, [masterDataLoading, id, zones.length]);

  useEffect(() => {
    if (!insightMatchReady) {
      setInsights({
        jobs: [],
        total: 0,
        salaryMin: null,
        salaryMax: null,
        salaryAverageMin: null,
        salaryAverageMax: null,
        reachScore: 0,
        reachStatus: "low",
        averageSkillMatch: null,
      });
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setInsightsLoading(true);
        const params = new URLSearchParams({
          page: "1",
          limit: "100",
        });

        if (formData.jobRole) {
          params.set("jobRole", formData.jobRole);
        }

        if (formData.experienceMin !== "" && formData.experienceMin !== null) {
          params.set("experienceMin", String(formData.experienceMin));
        }

        if (formData.experienceMax !== "" && formData.experienceMax !== null) {
          params.set("experienceMax", String(formData.experienceMax));
        }

        if (formData.skillsRequired.length > 0) {
          params.set("skills", formData.skillsRequired.join(","));
        }

        const authConfig = await getJobPortalAuthConfig();
        const response = await axiosInstance.get(
          `/job-portal/jobs/insights?${params.toString()}`,
          authConfig
        );

        setInsights({
          jobs: response.data?.data?.jobs || [],
          total: response.data?.data?.total || 0,
          salaryMin: response.data?.data?.salaryMin ?? null,
          salaryMax: response.data?.data?.salaryMax ?? null,
          salaryAverageMin: response.data?.data?.salaryAverageMin ?? null,
          salaryAverageMax: response.data?.data?.salaryAverageMax ?? null,
          reachScore: response.data?.data?.reachScore ?? 0,
          reachStatus: response.data?.data?.reachStatus ?? "low",
          averageSkillMatch: response.data?.data?.averageSkillMatch ?? null,
        });
      } catch (error) {
        console.error(error);
        setInsights({
          jobs: [],
          total: 0,
          salaryMin: null,
          salaryMax: null,
          salaryAverageMin: null,
          salaryAverageMax: null,
          reachScore: 0,
          reachStatus: "low",
          averageSkillMatch: null,
        });
      } finally {
        setInsightsLoading(false);
      }
    }, 450);

    return () => clearTimeout(timeoutId);
  }, [
    insightMatchReady,
    formData.experienceMin,
    formData.experienceMax,
    formData.jobRole,
    formData.skillsRequired,
  ]);

  const submitForm = async () => {
    if (!isEditMode && subscription?.status !== "active") {
      toast.error("An active recruiter plan is required to post jobs");
      navigate("/recruiter/plans");
      return;
    }

    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const payload = {
        jobTitle: formData.jobTitle,
        jobRole: formData.jobRole,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        statename: formData.statename,
        city: formData.city,
        country: formData.country,
        pincode: formData.pincode,
        latitude: formData.latitude,
        longitude: formData.longitude,
        zone: formData.zone,
        openings: Number(formData.openings),
        experienceMin: Number(formData.experienceMin),
        experienceMax: Number(formData.experienceMax),
        salaryMin: Number(formData.salaryMin),
        salaryMax: Number(formData.salaryMax),
        qualification: formData.qualification,
        qualifications: formData.qualifications,
        qualificationDetails: formData.qualificationDetails,
        genderRequirement: Number(formData.genderRequirement),
        englishRequirement: Number(formData.englishRequirement),
        relocationAllowed: formData.relocationAllowed,
        jobDescription: formData.jobDescription,
        companyName: formData.companyName,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        companyProfile: formData.companyProfile,
        companySize: formData.companySize,
        skillsRequired: formData.skillsRequired,
        jobStatus: formData.jobStatus,
        adminApprovalStatus: formData.adminApprovalStatus,
      };

      if (isEditMode) {
        await axiosInstance.put(`/job-portal/jobs/${id}`, payload, authConfig);
        toast.success("Job updated successfully");
      } else {
        await axiosInstance.post("/job-portal/jobs", payload, authConfig);
        toast.success("Job posted successfully");
      }

      navigate("/recruiter/jobs");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Unable to save job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h1 className="h4 mb-1">{isEditMode ? "Edit Job" : "Post a New Job"}</h1>
            <p className="text-muted mb-0">
              Create clear job posts and keep all recruiter-owned fields in one place.
            </p>
          </div>
          <Link to="/recruiter/jobs" className="btn btn-outline-primary">
            Back to Jobs
          </Link>
        </div>

        {!isEditMode && subscription?.status !== "active" && (
          <div className="alert alert-warning border-0 shadow-sm">
            An active recruiter plan is required to post a new job.
            <Link to="/recruiter/plans" className="ms-2 alert-link">
              Buy Plan
            </Link>
          </div>
        )}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {masterDataLoading ? (
              <div>Loading form...</div>
            ) : (
              <div className="row g-4">
                <div className="col-lg-8">
                  <div className="row g-3">
                      {formData.jobRole && (
                      <div className="col-md-6">
                        <label className="form-label">Job Title</label>
                        <input className="form-control" name="jobTitle" value={formData.jobTitle} onChange={handleChange} />
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">
                        Job Role / Area of Work <span className="text-danger">*</span>
                      </label>
                      <Select
                        classNamePrefix="job-role-select"
                        isClearable
                        isSearchable
                        name="jobRole"
                        value={selectedJobRoleOption}
                        onChange={handleJobRoleChange}
                        options={jobRoleOptions}
                        placeholder="Select Job Role"
                        styles={jobRoleSelectStyles}
                        noOptionsMessage={() => "No job roles found"}
                      />
                    </div>
                
                    <RadioCardGroup
                      label="Employment Type"
                      name="employmentType"
                      value={formData.employmentType}
                      options={employmentTypeOptions}
                      onChange={handleChange}
                      columns="col-md-4 col-xl-3"
                    />
                    <RadioCardGroup
                      label="Work Mode"
                      name="workMode"
                      value={formData.workMode}
                      options={workModeOptions}
                      onChange={handleChange}
                      columns="col-md-4"
                    />
                    <div className="col-md-4">
                      <label className="form-label">Job Status</label>
                      <select className="form-select" name="jobStatus" value={formData.jobStatus} onChange={handleChange}>
                        {jobStatusUpdateOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <LocationPicker
                        value={formData.address}
                        zones={zones}
                        onSelect={applyJobLocation}
                        label="Search job address on map"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Custom Address</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter or edit the job address"
                      />
                      <small className="text-muted">
                        Map search can fill location details; you can edit the saved address here.
                      </small>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Openings</label>
                      <input className="form-control" type="number" name="openings" value={formData.openings} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Min Experience</label>
                      <input className="form-control" type="number" name="experienceMin" value={formData.experienceMin} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Max Experience</label>
                      <input className="form-control" type="number" name="experienceMax" value={formData.experienceMax} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Qualification</label>
                      <Select
                        isMulti
                        isSearchable
                        closeMenuOnSelect={false}
                        options={educationOptions}
                        value={selectedQualificationOptions}
                        onChange={handleQualificationChange}
                        placeholder="Select qualification"
                      />
                    </div>
                    {selectedQualificationOptions.length > 0 && (
                      <div className="col-12">
                        <div className="border rounded p-3 bg-light">
                          <div className="fw-semibold mb-3">
                            Qualification Details
                          </div>
                          {selectedQualificationOptions.map((option) => {
                            const detail =
                              (formData.qualificationDetails || []).find(
                                (item) => item.qualification === option.value
                              ) || {};

                            return (
                              <div
                                className="row g-3 mb-3 border-bottom pb-3"
                                key={option.value}
                              >
                                <div className="col-12">
                                  <span className="badge bg-primary">
                                    {option.label}
                                  </span>
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Start Date</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={detail.startDate || ""}
                                    onChange={(event) =>
                                      updateQualificationDetail(
                                        option.value,
                                        "startDate",
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">End Date</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={detail.endDate || ""}
                                    onChange={(event) =>
                                      updateQualificationDetail(
                                        option.value,
                                        "endDate",
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">
                                    College / Institute
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={detail.collegeOrInstitute || ""}
                                    onChange={(event) =>
                                      updateQualificationDetail(
                                        option.value,
                                        "collegeOrInstitute",
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">School Name</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={detail.schoolName || ""}
                                    onChange={(event) =>
                                      updateQualificationDetail(
                                        option.value,
                                        "schoolName",
                                        event.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="col-md-3">
                      <label className="form-label">Min Salary</label>
                      <input className="form-control" type="number" name="salaryMin" value={formData.salaryMin} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Max Salary</label>
                      <input className="form-control" type="number" name="salaryMax" value={formData.salaryMax} onChange={handleChange} />
                    </div>
                    <RadioCardGroup
                      label="Gender"
                      name="genderRequirement"
                      value={formData.genderRequirement}
                      options={genderOptions}
                      onChange={handleChange}
                      columns="col-md-4"
                    />
                    <RadioCardGroup
                      label="English"
                      name="englishRequirement"
                      value={formData.englishRequirement}
                      options={englishOptions}
                      onChange={handleChange}
                      columns="col-md-6 col-xl-4"
                    />
                    <div className="col-12">
                      <label className="form-label d-block">Skills Required</label>
                      {!formData.jobRole ? (
                        <div className="text-muted small border rounded p-3 bg-light">
                          Select a Job Role to show matching skills.
                        </div>
                      ) : skills.length > 0 ? (
                        skills.map((skill) => (
                          <label key={skill._id} className="btn btn-sm btn-outline-primary me-2 mb-2">
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              name="skillsRequired"
                              value={skill.name}
                              checked={formData.skillsRequired.includes(skill.name)}
                              onChange={handleChange}
                            />
                            {skill.name}
                          </label>
                        ))
                      ) : (
                        <div className="text-muted small border rounded p-3 bg-light">
                          No predefined skills found for this Job Role.
                        </div>
                      )}
                      <div className="input-group mt-2">
                        <input
                          className="form-control"
                          value={customSkill}
                          onChange={(event) => setCustomSkill(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              addCustomSkill();
                            }
                          }}
                          placeholder="Add custom skill"
                        />
                        <button
                          className="btn btn-outline-primary"
                          type="button"
                          onClick={addCustomSkill}
                        >
                          Add
                        </button>
                      </div>
                      {formData.skillsRequired.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {formData.skillsRequired.map((skill) => (
                            <span
                              key={skill}
                              className="badge bg-primary d-inline-flex align-items-center gap-2"
                            >
                              {skill}
                              <button
                                className="btn-close btn-close-white"
                                type="button"
                                aria-label={`Remove ${skill}`}
                                onClick={() => removeSkill(skill)}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Job Description</label>
                      <textarea className="form-control" rows={4} name="jobDescription" value={formData.jobDescription} onChange={handleChange} />
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="mb-3" style={insightCardStyle}>
                    <div className="mb-3">
                      <div className="fw-semibold text-dark">Introducing</div>
                      <h2 className="h4 text-primary mb-0">
                        Performance Insights Hub
                      </h2>
                    </div>

                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h3 className="h5 text-primary mb-0">Job Reach Meter</h3>
                      <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-secondary text-white"
                        style={{ width: 16, height: 16, fontSize: 11 }}
                        title="Based on jobs matching job role, experience and at least 50% of selected skills"
                      >
                        i
                      </span>
                    </div>

                    <div
                      className="d-flex align-items-center gap-3 mb-4"
                      style={reachPanelStyle}
                    >
                      <div style={{ width: 150 }}>
                        <div
                          style={{
                            width: 116,
                            height: 58,
                            borderRadius: "116px 116px 0 0",
                            background:
                              "conic-gradient(from 270deg at 50% 100%, #ef4444 0deg 55deg, #f9ab00 55deg 105deg, #4caf50 105deg 180deg, transparent 180deg 360deg)",
                            position: "relative",
                            margin: "0 auto",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: "50%",
                              bottom: 0,
                              width: 4,
                              height: 54,
                              background: "#242424",
                              borderRadius: 4,
                              transformOrigin: "bottom center",
                              transform: reachStatus.needle,
                            }}
                          />
                        </div>
                        <div
                          className="text-center fw-semibold mt-1"
                          style={{ color: reachStatus.color }}
                        >
                          {insightsLoading
                            ? "Checking..."
                            : insightMatchReady
                            ? reachStatus.label
                            : "--"}
                        </div>
                        {insightMatchReady && !insightsLoading && (
                          <div className="text-center small text-muted">
                            Score {insights.reachScore || 0}/100
                          </div>
                        )}
                      </div>
                      <div className="text-dark">
                        {insightMatchReady
                          ? reachStatus.helper
                          : "Choose job role and experience range to see reach"}
                      </div>
                    </div>

                    <div style={salaryTrendStyle}>
                      <div className="d-flex gap-2 align-items-start mb-3">
                        <i className="ri-line-chart-line fs-5 text-dark" />
                        <div className="h5 mb-0">
                          Salary suggestion from matching jobs
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-6">
                          <div className="fw-bold text-dark">Market Avg Min</div>
                          <div className="text-muted">
                            {insightsLoading
                              ? "Loading..."
                              : formatCurrency(insights.salaryAverageMin)}
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="fw-bold text-dark">Market Avg Max</div>
                          <div className="text-muted">
                            {insightsLoading
                              ? "Loading..."
                              : formatCurrency(insights.salaryAverageMax)}
                          </div>
                        </div>
                      </div>
                      {insightMatchReady && (
                        <>
                          <div className="small text-muted mt-3">
                            {insightsLoading
                              ? "Fetching matching jobs..."
                              : `${insights.total} matching jobs found by role, experience and 50% skills`}
                          </div>
                          <div
                            className={`alert alert-${
                              salaryRecommendation.tone === "danger"
                                ? "danger"
                                : salaryRecommendation.tone === "success"
                                ? "success"
                                : salaryRecommendation.tone === "primary"
                                ? "primary"
                                : "secondary"
                            } mt-3 mb-0 py-2`}
                          >
                            <div className="fw-semibold">
                              {salaryRecommendation.title}
                            </div>
                            <div className="small">
                              {salaryInsightReady
                                ? salaryRecommendation.message
                                : "Enter minimum and maximum salary to get increase/decrease suggestion."}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="border rounded p-3">
                    <h2 className="h6 mb-3">Company Details</h2>
                    <div className="mb-3">
                      <label className="form-label">Organization Name</label>
                      <input className="form-control" name="companyName" value={formData.companyName} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Contact Person</label>
                      <input className="form-control" name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input className="form-control" name="phone" value={formData.phone} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input className="form-control" name="email" value={formData.email} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Size</label>
                      <input className="form-control" name="companySize" value={formData.companySize} onChange={handleChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Profile</label>
                      <textarea className="form-control" rows={3} name="companyProfile" value={formData.companyProfile} onChange={handleChange} />
                    </div>
                    <div className="form-check mb-3">
                      <input className="form-check-input" type="checkbox" id="relocationAllowed" name="relocationAllowed" checked={formData.relocationAllowed} onChange={handleChange} />
                      <label className="form-check-label" htmlFor="relocationAllowed">
                        Allow relocation applicants
                      </label>
                    </div>
                    <button
                      className="btn btn-primary w-100"
                      onClick={submitForm}
                      disabled={loading || (!isEditMode && subscription?.status !== "active")}
                    >
                      {loading ? "Saving..." : isEditMode ? "Update Job" : "Create Job"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default RecruiterJobForm;
